import { useState, useEffect, useCallback } from "react";
import api from "../../../../services/api";
import { showBasicAlert } from "../../../../core/common/useAlert";
import type { Company, User, Todo } from "../types";

interface AllData {
  companies: Company[];
  users: User[];
  todos: Todo[];
}

interface CompanyOption {
  value: string;
  label: string;
}

/**
 * Shared data + mutation layer for the Task feature.
 *
 * Owns the company/user/todo datasets and every server interaction that is
 * identical between the List and Grid views (fetch, company selection, read /
 * stage / delete / transfer mutations). View-specific concerns — filter state,
 * sorting and rendering — stay in the individual view components.
 *
 * Company select options are expected to use a `"<id>-<type>"` value so the
 * company type can be recovered without a lookup.
 */
export const useTodoData = () => {
  const signature = localStorage.getItem("signature");

  const [allData, setAllData] = useState<AllData>({
    companies: [],
    users: [],
    todos: [],
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(
    null,
  );
  const [selectedCompanyType, setSelectedCompanyType] = useState<string | null>(
    null,
  );

  // Fetch companies + users once on mount.
  const fetchAllData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [companiesResponse, usersResponse] = await Promise.all([
        api.post<{ data: { data: Company[] } }>(
          "userService/getAllCompanyList",
          { signature },
        ),
        api.post<{ data: { data: { allUsers: User[] } } }>(
          "userService/getEmployeesListData",
          { signature },
        ),
      ]);

      setAllData((prev) => ({
        ...prev,
        companies: companiesResponse?.data?.data?.data,
        users: usersResponse?.data?.data?.data?.allUsers,
      }));
    } catch (error) {
      setIsLoading(false);
      showBasicAlert("Failed to fetch data. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [signature]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  /** Fetch the todos for a given company/type pair. */
  const loadTodos = useCallback(
    async (companyId: string | null, companyType: string | null) => {
      try {
        setIsLoading(true);
        const todosResponse = await api.post<{ data: { data: Todo[] } }>(
          "todoService/allTodos",
          { signature, company_id: companyId, company_type: companyType },
        );
        setAllData((prev) => ({
          ...prev,
          todos: todosResponse?.data?.data?.data,
        }));
      } catch (error) {
        showBasicAlert("Failed to fetch data. Please try again.");
      } finally {
        setIsLoading(false);
      }
    },
    [signature],
  );

  /**
   * Handle a company selection from a CommonSelect built with
   * `value: "<id>-<type>"`. The optional callback lets a view sync its own
   * filter state with the new company id.
   */
  const handleCompanySelect = useCallback(
    async (
      option: CompanyOption | null,
      onSelected?: (companyId: string | null, companyType: string | null) => void,
    ) => {
      if (!option) {
        setSelectedCompanyId(null);
        setSelectedCompanyType(null);
        return;
      }
      const [rawId, rawType] = (option.value ?? "").split("-");
      const companyId = rawId?.trim() || null;
      const companyType = rawType?.trim() || null;

      setSelectedCompanyId(companyId);
      setSelectedCompanyType(companyType);
      onSelected?.(companyId, companyType);

      await loadTodos(companyId, companyType);
    },
    [loadTodos],
  );

  /** Re-fetch the current company's todos (after create/edit/delete). */
  const refreshTodos = useCallback(() => {
    return loadTodos(selectedCompanyId, selectedCompanyType);
  }, [loadTodos, selectedCompanyId, selectedCompanyType]);

  /** Mark a todo as read locally + on the server. */
  const updateReadStatus = useCallback(
    async (todoId: string) => {
      const response = await api.post("todoService/updateTodoReadStatus", {
        signature,
        id: todoId,
      });
      if (response?.data?.status || response?.data?.message?.[0]?.status) {
        setAllData((prev) => ({
          ...prev,
          todos: prev.todos.map((todo) =>
            todo.id === todoId ? { ...todo, isRead: true } : todo,
          ),
        }));
      }
      return response?.data;
    },
    [signature],
  );

  /** Update a todo's stage. */
  const handleStageUpdate = useCallback(
    async (todoId: string, newStage: string) => {
      try {
        setIsLoading(true);
        const response = await api.post("todoService/updateTodoStage", {
          signature,
          id: todoId,
          stage: parseInt(newStage),
        });
        showBasicAlert(response?.data?.message?.[0]?.msg);
        setAllData((prev) => ({
          ...prev,
          todos: prev.todos.map((todo) =>
            todo.id === todoId ? { ...todo, stage: parseInt(newStage) } : todo,
          ),
        }));
      } catch (error) {
        showBasicAlert("Failed to update stage. Please try again.");
      } finally {
        setIsLoading(false);
      }
    },
    [signature],
  );

  /** Delete a todo and drop it from local state. */
  const handleDeleteTodo = useCallback(
    async (deleteTodoId: string | null) => {
      if (!deleteTodoId) return;
      try {
        setIsLoading(true);
        const response = await api.post("todoService/deleteTodos", {
          signature,
          id: deleteTodoId,
        });
        showBasicAlert(response?.data?.message?.[0]?.msg);
        setAllData((prev) => ({
          ...prev,
          todos: prev.todos.filter((todo) => todo.id !== deleteTodoId),
        }));
      } catch (error) {
        showBasicAlert("Failed to delete todo");
      } finally {
        setIsLoading(false);
      }
    },
    [signature],
  );

  /** Reflect a completed transfer in local state without a refetch. */
  const handleTaskTransferred = useCallback(
    (
      todoId: string,
      fromMemberIds: string[],
      toMembers: Array<{ id: string; name: string }>,
    ) => {
      setAllData((prev) => ({
        ...prev,
        todos: prev.todos.map((todo) => {
          if (todo.id !== todoId) return todo;
          const kept = todo.assigned_members.filter(
            (m) => !fromMemberIds.includes(m.id?.toString()),
          );
          const merged = [...kept];
          toMembers.forEach((tm) => {
            if (!merged.some((m) => m.id?.toString() === tm.id?.toString())) {
              merged.push({ id: tm.id, name: tm.name });
            }
          });
          return { ...todo, assigned_members: merged };
        }),
      }));
    },
    [],
  );

  /** Selected company record (for TodoModal). */
  const selectedCompany = allData.companies.find(
    (company) =>
      company.id == selectedCompanyId && company.type == selectedCompanyType,
  );

  return {
    signature,
    allData,
    setAllData,
    isLoading,
    setIsLoading,
    selectedCompanyId,
    selectedCompanyType,
    selectedCompany,
    fetchAllData,
    handleCompanySelect,
    refreshTodos,
    updateReadStatus,
    handleStageUpdate,
    handleDeleteTodo,
    handleTaskTransferred,
  };
};

export default useTodoData;
